// Guard expression language — spec §6.5, D6-5. Deliberately tiny: no loops, no
// recursion, no function calls, no assignment, no side effects; evaluates in linear
// time over a fixed context. Grammar (precedence low → high):
//
//   or      := and ("||" and)*
//   and     := not ("&&" not)*
//   not     := "!" not | cmp
//   cmp     := sum (("==" | "!=" | "<" | "<=" | ">" | ">=" | "in") sum)?
//   sum     := term (("+" | "-") term)*
//   term    := unary (("*" | "/") unary)*
//   unary   := "-" unary | primary
//   primary := number | string | "true" | "false" | "null" | path | "(" or ")" | "[" (or ("," or)*)? "]"
//   path    := ident ("." ident)*
//
// Paths read from the context object; a missing path evaluates to null (never throws).
// A path segment applied to an array projects it: `items.containerId` is the list of
// each item's containerId (the one list operation a flow needs, e.g. to name the
// children of an aggregation).
const OPS = ["||", "&&", "==", "!=", "<=", ">=", "<", ">", "!", "+", "-", "*", "/", "(", ")", "[", "]", ",", "."];
export class ExprError extends Error {
    position;
    constructor(message, position) {
        super(`${message} at ${position}`);
        this.position = position;
        this.name = "ExprError";
    }
}
function tokenize(src) {
    const out = [];
    let i = 0;
    while (i < src.length) {
        const c = src[i];
        if (c === " " || c === "\t" || c === "\n") {
            i++;
            continue;
        }
        if (/[0-9]/.test(c)) {
            const m = /^\d+(\.\d+)?/.exec(src.slice(i));
            out.push({ t: "num", v: Number(m[0]) });
            i += m[0].length;
            continue;
        }
        if (c === '"' || c === "'") {
            let j = i + 1;
            let s = "";
            while (j < src.length && src[j] !== c) {
                if (src[j] === "\\" && j + 1 < src.length)
                    j++;
                s += src[j];
                j++;
            }
            if (j >= src.length)
                throw new ExprError("unterminated string", i);
            out.push({ t: "str", v: s });
            i = j + 1;
            continue;
        }
        if (/[A-Za-z_]/.test(c)) {
            const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
            out.push({ t: "id", v: m[0] });
            i += m[0].length;
            continue;
        }
        const op = OPS.find((o) => src.startsWith(o, i));
        if (!op)
            throw new ExprError(`unexpected character '${c}'`, i);
        out.push({ t: "op", v: op });
        i += op.length;
    }
    out.push({ t: "eof" });
    return out;
}
class Parser {
    toks;
    i = 0;
    constructor(toks) {
        this.toks = toks;
    }
    parse() {
        const n = this.or();
        if (this.peek().t !== "eof")
            throw new ExprError("unexpected trailing input", this.i);
        return n;
    }
    peek() {
        return this.toks[this.i];
    }
    isOp(v) {
        const t = this.peek();
        return t.t === "op" && t.v === v;
    }
    isId(v) {
        const t = this.peek();
        return t.t === "id" && t.v === v;
    }
    take() {
        return this.toks[this.i++];
    }
    or() {
        let a = this.and();
        while (this.isOp("||")) {
            this.take();
            a = { k: "bin", op: "||", a, b: this.and() };
        }
        return a;
    }
    and() {
        let a = this.not();
        while (this.isOp("&&")) {
            this.take();
            a = { k: "bin", op: "&&", a, b: this.not() };
        }
        return a;
    }
    not() {
        if (this.isOp("!")) {
            this.take();
            return { k: "un", op: "!", a: this.not() };
        }
        return this.cmp();
    }
    cmp() {
        const a = this.sum();
        const t = this.peek();
        if ((t.t === "op" && ["==", "!=", "<", "<=", ">", ">="].includes(t.v)) || (t.t === "id" && t.v === "in")) {
            this.take();
            return { k: "bin", op: t.v, a, b: this.sum() };
        }
        return a;
    }
    sum() {
        let a = this.term();
        while (this.isOp("+") || this.isOp("-")) {
            const op = this.take().v;
            a = { k: "bin", op, a, b: this.term() };
        }
        return a;
    }
    term() {
        let a = this.unary();
        while (this.isOp("*") || this.isOp("/")) {
            const op = this.take().v;
            a = { k: "bin", op, a, b: this.unary() };
        }
        return a;
    }
    unary() {
        if (this.isOp("-")) {
            this.take();
            return { k: "un", op: "-", a: this.unary() };
        }
        return this.primary();
    }
    primary() {
        const t = this.take();
        if (t.t === "num")
            return { k: "lit", v: t.v };
        if (t.t === "str")
            return { k: "lit", v: t.v };
        if (t.t === "id") {
            if (t.v === "true")
                return { k: "lit", v: true };
            if (t.v === "false")
                return { k: "lit", v: false };
            if (t.v === "null")
                return { k: "lit", v: null };
            const p = [t.v];
            while (this.isOp(".")) {
                this.take();
                const n = this.take();
                if (n.t !== "id")
                    throw new ExprError("expected identifier after '.'", this.i);
                p.push(n.v);
            }
            return { k: "path", p };
        }
        if (t.t === "op" && t.v === "(") {
            const n = this.or();
            if (!this.isOp(")"))
                throw new ExprError("expected ')'", this.i);
            this.take();
            return n;
        }
        if (t.t === "op" && t.v === "[") {
            const items = [];
            if (!this.isOp("]")) {
                items.push(this.or());
                while (this.isOp(",")) {
                    this.take();
                    items.push(this.or());
                }
            }
            if (!this.isOp("]"))
                throw new ExprError("expected ']'", this.i);
            this.take();
            return { k: "list", items };
        }
        throw new ExprError("unexpected token", this.i);
    }
}
/** Parse at publish time; throws ExprError on invalid syntax. */
export function parseExpr(src) {
    return new Parser(tokenize(src)).parse();
}
function lookup(ctx, path) {
    let cur = ctx;
    for (const key of path) {
        if (cur === null || typeof cur !== "object")
            return null;
        if (Array.isArray(cur)) {
            cur = cur.map((item) => (item !== null && typeof item === "object" && !Array.isArray(item) ? item[key] ?? null : null));
            continue;
        }
        cur = cur[key] ?? null;
    }
    return cur;
}
function truthy(v) {
    return v !== null && v !== false && v !== 0 && v !== "";
}
function eq(a, b) {
    if (Array.isArray(a) || Array.isArray(b) || (a && typeof a === "object") || (b && typeof b === "object")) {
        return JSON.stringify(a) === JSON.stringify(b);
    }
    if (a === b)
        return true;
    // ERP fields arrive as text ("13") while a rule says 13 (owner 2026-11-03: Trans.grupp 2 == 13 must match):
    // a number and a numeric string are equal when they mean the same number.
    if ((typeof a === "number" && typeof b === "string") || (typeof a === "string" && typeof b === "number")) {
        const s = String(typeof a === "string" ? a : b).trim();
        return s !== "" && Number.isFinite(Number(s)) && Number(s) === (typeof a === "number" ? a : b);
    }
    return false;
}
function cmpNum(op, a, b) {
    // Numbers compare numerically; strings (e.g. ISO dates) lexically; mixed → false.
    if (typeof a === "number" && typeof b === "number")
        return op === "<" ? a < b : op === "<=" ? a <= b : op === ">" ? a > b : a >= b;
    if (typeof a === "string" && typeof b === "string")
        return op === "<" ? a < b : op === "<=" ? a <= b : op === ">" ? a > b : a >= b;
    return false;
}
export function evalNode(n, ctx) {
    switch (n.k) {
        case "lit":
            return n.v;
        case "path":
            return lookup(ctx, n.p);
        case "list":
            return n.items.map((i) => evalNode(i, ctx));
        case "un": {
            const a = evalNode(n.a, ctx);
            return n.op === "!" ? !truthy(a) : typeof a === "number" ? -a : null;
        }
        case "bin": {
            if (n.op === "&&")
                return truthy(evalNode(n.a, ctx)) ? evalNode(n.b, ctx) : false;
            if (n.op === "||") {
                const a = evalNode(n.a, ctx);
                return truthy(a) ? a : evalNode(n.b, ctx);
            }
            const a = evalNode(n.a, ctx);
            const b = evalNode(n.b, ctx);
            switch (n.op) {
                case "==":
                    return eq(a, b);
                case "!=":
                    return !eq(a, b);
                case "<":
                case "<=":
                case ">":
                case ">=":
                    return cmpNum(n.op, a, b);
                case "in":
                    return Array.isArray(b) ? b.some((x) => eq(x, a)) : typeof b === "string" && typeof a === "string" ? b.includes(a) : false;
                case "+":
                    return typeof a === "number" && typeof b === "number" ? a + b : typeof a === "string" && typeof b === "string" ? a + b : null;
                case "-":
                    return typeof a === "number" && typeof b === "number" ? a - b : null;
                case "*":
                    return typeof a === "number" && typeof b === "number" ? a * b : null;
                case "/":
                    return typeof a === "number" && typeof b === "number" && b !== 0 ? a / b : null;
                default:
                    return null;
            }
        }
    }
}
/** Evaluate an expression against a context; truthiness per `truthy`. */
export function evaluate(src, ctx) {
    return evalNode(parseExpr(src), ctx);
}
export function evaluateBoolean(src, ctx) {
    return truthy(evaluate(src, ctx));
}
//# sourceMappingURL=expr.js.map